import {
  usePurchase,
  usePurchaseUpdate,
} from '@/core/domain/purchase/Purchase.hooks';
import {
  Purchase,
  PurchaseCreateFormValues,
  PurchaseItemTaxes,
  PurchaseUpdateRequest,
} from '@/core/domain/purchase/Purchase.types';
import { Form, FormInstance } from 'antd';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import React, {
  PropsWithChildren,
  useState,
  createContext,
  useContext,
  useEffect,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import { PurchaseFinancialFormValues } from '../PurchaseDetailsPage/PurchaseFinancialPage';
import { Status } from '@/utils/enum/statusEnum';
import { useItemsPurchases } from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { PurchaseItem } from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';

type Props = PropsWithChildren<unknown>;

type Forms = {
  releasePurchaseGeneral?: PurchaseCreateFormValues;
  purchaseFinancialForm?: PurchaseFinancialFormValues;
  purchaseTaxesForm?: PurchaseItemTaxes;
};

type InstanceForms = {
  releasePurchaseGeneral?: FormInstance<PurchaseCreateFormValues>;
  purchaseFinancialForm?: FormInstance<PurchaseFinancialFormValues>;
  purchaseDataFreight?: PurchaseUpdateRequest;
  purchaseTaxesForm?: FormInstance<PurchaseItemTaxes>;
};

type ReleasePurchaseContextProps = {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  contextForm: Forms;
  purchaseId: string;
  disabled: boolean;
  handleSubmit: () => void;
  situation?: Status;
  instanceForms: InstanceForms;
  purchase?: Purchase;
  loadingPurchase?: boolean;
  itemsPurchase?: PurchaseItem[];
};

const ReleasePurchaseContext = createContext({} as ReleasePurchaseContextProps);
export const useReleasePurchaseFormContext = () =>
  useContext(ReleasePurchaseContext);

const ReleasesPurchaseFormProvider: React.FC<Props> = ({ children }) => {
  const { purchaseId } = useRouteMatch<{ purchaseId: string }>().params;
  const purchase = usePurchase(Number(purchaseId));

  const itemsPurchaseList = useItemsPurchases({
    order: 'DESC',
    purchaseId: Number(purchaseId),
    enabled: !!purchase.data,
  });

  const itemsPurchase = flatInfiniteData(itemsPurchaseList.data);

  const [contextForm, setContextForm] = useState<Forms>({});
  const [isLoading, setIsLoading] = useState(false);
  const [situation, setSituation] = useState<Status>();
  const [instanceForms, setInstanceForms] = useState<InstanceForms>({});

  const releasePurchaseUpdate = usePurchaseUpdate();

  const disabled = !Object.keys(contextForm).some((key) => !!key);

  useEffect(() => {
    setContextForm({});
    setInstanceForms({});
  }, [purchaseId]);

  useEffect(() => {
    if (purchase.data?.situation) {
      setSituation(purchase.data.situation);
    }
  }, [purchase.data?.situation]);

  function handleFormChange(name: string, info: FormChangeInfo) {
    setInstanceForms((state) => ({ ...state, [name]: info.forms[name] }));
    if (name && info?.forms[name]) {
      setContextForm((state) => ({
        ...state,
        [name]: info.forms[name]?.getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    try {
      setIsLoading(true);
      if (contextForm.releasePurchaseGeneral) {
        const { supplier, ...values } = contextForm.releasePurchaseGeneral;

        const obj: PurchaseUpdateRequest = {
          ...purchase.data,
          purchaseItems: [],
          ...values,
          deliveryPrevisionDate:
            values.deliveryPrevisionDate?.format('YYYY-MM-DD'),
          id: Number(purchaseId),
          supplierDTO: { id: supplier.id },
          issueDate: values.issueDate.toISOString(),
          releaseDate: values.releaseDate?.toISOString(),
          departureDate: values.departureDate?.toISOString(),
        };
        await releasePurchaseUpdate.mutateAsync(obj);
      }
    } catch {}
    setIsLoading(false);
    setContextForm({});
  }

  return (
    <ReleasePurchaseContext.Provider
      value={{
        purchaseId,
        contextForm,
        disabled,
        handleSubmit,
        isLoading,
        setIsLoading,
        situation,
        instanceForms,
        purchase: purchase.data,
        loadingPurchase: purchase.isLoading,
        itemsPurchase,
      }}
    >
      <Form.Provider
        onFormChange={handleFormChange}
        onFormFinish={handleSubmit}
      >
        {children}
      </Form.Provider>
    </ReleasePurchaseContext.Provider>
  );
};

export default ReleasesPurchaseFormProvider;
